import { useState, useEffect, useMemo } from "react";

// @mui material components
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';



// Material Dashboard 2 React components
import MDTypography from "components/atoms/MDTypography/MDTypography";
import MDInput from "components/atoms/MDInput/MDInput";
import MDPagination from "components/atoms/MDPagination/MDPagination";
import MDButton from "components/atoms/MDButton/MDButton";
import MDBox from "components/atoms/MDBox/MDBox";

// Custom head and body cells
import DataTableHeadCell from "./DataTableHeadCell";
import DataTableBodyCell from "./DataTableBodyCell";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch, faCircleUser } from "@fortawesome/free-solid-svg-icons";
import ErrorRetry from "components/molecules/ErrorRetry/ErrorRetry";
import colors from "assets/theme/base/colors";
import layout from "assets/theme/base/layout";
import { Breakpoints, MUIColors } from "models/StyleModels";
import { useBreakpoints } from "hooks/useBreakpoints";

// Styled components
import {
    StyledTableContainer,
    StyledTable,
    StyledEntriesAutocomplete,
    StyledGridContainer,
    StyledSearchContainer,
    StyledUserIcon,
    StyledFallbackContainer,
    StyledEmptyContainer,
    StyledPaginationContainer,
    StyledEntriesInfo,
    StyledEntriesText,
    StyledPaginationInputContainer,
    StyledChevronIcon,
    StyledDataTableRow,
    StyledFlexContainer,
    StyledFlexAlignContainer,
    StyledPaddingContainer,
} from './DataTable.styles';

export interface TableColumnType {
    Header: string;
    accessor?: string;
    width?: string;
    align?: 'left' | 'center' | 'right';
    isSorted?: boolean;
    canSort?: boolean;
    isSortedDesc?: boolean;
    Cell?: React.ReactNode | ((props: { row: { original: any } }) => React.ReactNode);
}

export interface TableSecondaryAction {
    label: string;
    action(args?: any): any;
}



interface DataTableProps {
    entriesPerPage?: {
        defaultValue: number;
        entries: number[];
    };
    canSearch?: boolean;
    showTotalEntries?: boolean;
    table: {
        columns: TableColumnType[];
        rows: { [key: string]: React.ReactNode }[];
    };
    pagination?: {
        variant: 'contained' | 'gradient';
        color: MUIColors | string;
    };
    isSorted?: boolean[];
    noEndBorder?: boolean;
    dataLoading?: boolean;
    dataError?: boolean;
    handleRetry?(): void;
    sx?: { [key: string]: any };
    secondaryAction?: TableSecondaryAction | null;
    handleRowClick?(args: any): any;
    emptyMessage?: string;

    // When true, if there are no rows, show the empty state even if dataLoading is true
    preferEmptyOnNoRows?: boolean;

    // Custom props for extending functionality
    customSortLogic?: (column: TableColumnType) => boolean;
    getSortedValue?: (column: TableColumnType) => 'none' | 'asce' | 'desc' | false;
    searchPlaceholder?: string;
    tableContainerSx?: any;
    verticalAlign?: 'top' | 'middle';
    initialSort?: {
        key: string;
        direction: 'asc' | 'desc';
    };
    customSortFunction?: (a: any, b: any, key: string, direction: 'asc' | 'desc') => number;
    centerInputs?: boolean;
    getRowKey: (rowData: any, rowIndex: number) => string;
}

const DataTable: React.FC<DataTableProps> = ({
    entriesPerPage,
    canSearch,
    showTotalEntries,
    table,
    isSorted,
    noEndBorder,
    dataLoading,
    dataError,
    handleRetry,
    secondaryAction,
    handleRowClick,
    emptyMessage,
    customSortLogic,
    getSortedValue,
    searchPlaceholder = "Search",
    tableContainerSx,
    verticalAlign = 'middle',
    initialSort,
    customSortFunction,
    centerInputs = false,
    getRowKey,
    preferEmptyOnNoRows = false,
}) => {

    const defaultValue = entriesPerPage?.defaultValue ? entriesPerPage.defaultValue : 15;
    const entries = entriesPerPage?.entries
        ? entriesPerPage.entries.map((el) => el.toString())
        : ["10", "15", "25", "50", "100"];

    const { breakpointBreached } = useBreakpoints({ breakpoint: Breakpoints.LARGE });

    // State management
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(defaultValue);
    const [globalFilter, setGlobalFilter] = useState("");
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
    const [sortConfig, setSortConfig] = useState<{
        key: string;
        direction: 'asc' | 'desc';
    } | null>(null);



    // Set the default value for the entries per page when component mounts
    useEffect(() => {
        setPageSize(defaultValue || 15);
    }, [defaultValue]);

    useEffect(() => {
        if (initialSort && !sortConfig) {
            setSortConfig(initialSort);
        }
    }, [initialSort, sortConfig]);

    // Filter data based on global filter
    const filteredData = useMemo(() => {
        if (!globalFilter) return table.rows;

        return table.rows.filter((row) => {
            return Object.values(row).some((value) => {
                if (typeof value === 'string') {
                    return value.toLowerCase().includes(globalFilter.toLowerCase());
                }
                return false;
            });
        });
    }, [table.rows, globalFilter]);

    // Sort data
    const sortedData = useMemo(() => {
        if (!sortConfig) return filteredData;

        return [...filteredData].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            // Handle null/undefined values
            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
            if (bValue == null) return sortConfig.direction === 'asc' ? 1 : -1;

            if (customSortFunction) {
                return customSortFunction(a, b, sortConfig.key, sortConfig.direction);
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'desc' ? 1 : -1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'desc' ? -1 : 1;
            }
            return 0;
        });

    }, [filteredData, sortConfig, customSortFunction]);

    // Paginate data
    const paginatedData = useMemo(() => {
        const startIndex = pageIndex * pageSize;
        const endIndex = startIndex + pageSize;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, pageIndex, pageSize]);

    // Calculate pagination info
    const totalPages = Math.ceil(sortedData.length / pageSize);
    const canPreviousPage = pageIndex > 0;
    const canNextPage = pageIndex < totalPages - 1;


    const handleSort = (column: TableColumnType) => {
        if (customSortLogic && !customSortLogic(column)) {
            return;
        }

        if (!column.accessor) {
            return;
        }

        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === column.accessor && sortConfig.direction === 'asc') {
            direction = 'desc';
        }

        setSortConfig({ key: column.accessor, direction });
    };

    // Handle pagination
    const gotoPage = (page: number) => {
        setPageIndex(Math.max(0, Math.min(page, totalPages - 1)));
    };

    const nextPage = () => {
        if (canNextPage) {
            setPageIndex(pageIndex + 1);
        }
    };

    const previousPage = () => {
        if (canPreviousPage) {
            setPageIndex(pageIndex - 1);
        }
    };

    // Handle entries per page change
    const setEntriesPerPage = (value: number) => {
        setPageSize(value);
        setPageIndex(0); // Reset to first page when changing page size
    };

    // Handle search
    const handleSearchChange = (value: string) => {
        setGlobalFilter(value);
        setPageIndex(0); // Reset to first page when searching
    };

    // Render pagination buttons
    const renderPagination = () => {
        const pageOptions = Array.from({ length: totalPages }, (_, i) => i);

        return pageOptions.map((option: number) => (
            <MDPagination
                item
                key={option}
                onClick={() => gotoPage(option)}
                active={pageIndex === option}
            >
                {option + 1}
            </MDPagination>
        ));
    };

    // Calculate entries display
    const entriesStart = pageIndex === 0 ? 1 : pageIndex * pageSize + 1;
    const entriesEnd = Math.min((pageIndex + 1) * pageSize, sortedData.length);

    // Row hover handlers - now using React state instead of DOM manipulation
    const handleRowMouseEnter = (index: number) => {
        setHoveredRowIndex(index);
    };

    const handleRowMouseLeave = () => {
        setHoveredRowIndex(null);
    };

    return (
        <>
            {dataError && <ErrorRetry handleRetry={handleRetry ? handleRetry : () => { }} />}
            {!dataError && (
                <StyledTableContainer tableContainerSx={tableContainerSx}>
                    {entriesPerPage || canSearch ? (
                        <StyledFlexContainer>
                            {entriesPerPage && (
                                <StyledFlexAlignContainer>
                                    <StyledEntriesAutocomplete
                                        disableClearable
                                        value={pageSize.toString()}
                                        options={entries}
                                        onChange={(event, newValue) => {
                                            setEntriesPerPage(parseInt(newValue as string, 10));
                                        }}
                                        size="small"
                                        renderInput={(params) => <MDInput {...params} />}
                                    />
                                    <MDTypography variant="caption" color={MUIColors.SECONDARY}>
                                        &nbsp;&nbsp;entries per page
                                    </MDTypography>
                                </StyledFlexAlignContainer>
                            )}

                            <StyledPaddingContainer>
                                {centerInputs ? (
                                    <MDBox display="flex" justifyContent="center" flexDirection="column" alignItems="center" gap={2}>
                                        {canSearch && (
                                            <MDBox sx={{ maxWidth: layout.input.maxWidth, width: '100%' }}>
                                                <MDInput
                                                    placeholder={searchPlaceholder}
                                                    value={globalFilter}
                                                    size="small"
                                                    fullWidth
                                                    onChange={({ currentTarget }) => {
                                                        handleSearchChange(currentTarget.value);
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <SearchIcon fontSize="medium" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </MDBox>
                                        )}

                                        {secondaryAction && (
                                            <MDBox display="flex" gap={2} flexWrap="wrap" justifyContent="center">
                                                {secondaryAction && (
                                                    <MDButton
                                                        variant="contained"
                                                        color={MUIColors.SECONDARY}
                                                        onClick={secondaryAction.action}
                                                    >
                                                        <StyledUserIcon color={colors.white.focus} icon={faCircleUser} />
                                                        {secondaryAction.label}
                                                    </MDButton>
                                                )}
                                            </MDBox>
                                        )}
                                    </MDBox>
                                ) : (
                                    <StyledGridContainer breakpointBreached={breakpointBreached}>
                                        <StyledSearchContainer>
                                            {canSearch && (
                                                <MDInput
                                                    placeholder={searchPlaceholder}
                                                    value={globalFilter}
                                                    size="small"
                                                    fullWidth
                                                    onChange={({ currentTarget }) => {
                                                        handleSearchChange(currentTarget.value);
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <SearchIcon fontSize="medium" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        </StyledSearchContainer>

                                        {secondaryAction && (
                                            <div>
                                                <MDButton
                                                    variant="contained"
                                                    color={MUIColors.SECONDARY}
                                                    onClick={secondaryAction.action}
                                                    fullWidth
                                                >
                                                    <StyledUserIcon color={colors.white.focus} icon={faCircleUser} />
                                                    {secondaryAction.label}
                                                </MDButton>
                                            </div>
                                        )}
                                    </StyledGridContainer>
                                )}
                            </StyledPaddingContainer>
                        </StyledFlexContainer>
                    ) : null}

                    <StyledTable>
                        <thead>
                            <TableRow>
                                {table.columns.map((column, index) => {
                                    const canSort = column.canSort && column.accessor;
                                    const onClickHandler = canSort ? () => {
                                        handleSort(column);
                                    } : undefined;

                                    return (
                                        <DataTableHeadCell
                                            key={`header-${column.accessor || 'empty'}-${index}`}
                                            width={column.width || "auto"}
                                            align={(column.align as 'left' | 'center' | 'right') || "left"}
                                            sorted={
                                                getSortedValue
                                                    ? getSortedValue(column)
                                                    : sortConfig && sortConfig.key === column.accessor
                                                        ? sortConfig.direction === 'asc' ? 'asce' : 'desc'
                                                        : 'none'
                                            }
                                            onClick={onClickHandler}
                                        >
                                            {column.Header}
                                        </DataTableHeadCell>
                                    );
                                })}
                            </TableRow>
                        </thead>

                        {!dataLoading && (
                            <TableBody>
                                {paginatedData.map((row, rowIndex) => {
                                    const rowKey = getRowKey(row, rowIndex);

                                    return (
                                        <StyledDataTableRow
                                            key={rowKey}
                                            id={rowKey}
                                            onClick={handleRowClick ? () => handleRowClick(row) : undefined}
                                            onMouseEnter={() => handleRowMouseEnter(rowIndex)}
                                            onMouseLeave={handleRowMouseLeave}
                                            isHovered={hoveredRowIndex === rowIndex}
                                            isClickable={!!handleRowClick}
                                            hoverColor={colors.disabled.focus}
                                        >
                                            {table.columns.map((column, columnIndex) => (
                                                <DataTableBodyCell
                                                    key={`cell-${column.accessor || 'empty'}-${columnIndex}-${rowKey}`}
                                                    noBorder={noEndBorder && rowIndex === paginatedData.length - 1}
                                                    align={(column.align as 'left' | 'center' | 'right') || "left"}
                                                    verticalAlign={verticalAlign}
                                                >
                                                    {column.Cell
                                                        ? typeof column.Cell === 'function'
                                                            ? column.Cell({ row: { original: row } })
                                                            : column.Cell
                                                        : row[column.accessor || '']}
                                                </DataTableBodyCell>
                                            ))}
                                        </StyledDataTableRow>
                                    );
                                })}
                            </TableBody>
                        )}
                    </StyledTable>


                    {dataLoading && !(preferEmptyOnNoRows && !paginatedData.length) && (
                        <StyledFallbackContainer>
                            <FontAwesomeIcon icon={faCircleNotch} spin color="grey" size="2x" />
                        </StyledFallbackContainer>
                    )}

                    {!paginatedData.length && (!dataLoading || preferEmptyOnNoRows) && (
                        <StyledEmptyContainer>
                            <MDTypography textAlign='center' variant='body1' color={MUIColors.DULL}>
                                {emptyMessage || 'No data to display.'}
                            </MDTypography>
                        </StyledEmptyContainer>
                    )}

                    <StyledPaginationContainer
                        showTotalEntries={!!showTotalEntries}
                        totalPages={totalPages}
                    >
                        {showTotalEntries && !!sortedData.length && (
                            <StyledEntriesInfo>
                                <StyledEntriesText variant="button" color={MUIColors.SECONDARY} fontWeight="regular">
                                    Showing {entriesStart} to {entriesEnd} of {sortedData.length} entries
                                </StyledEntriesText>
                            </StyledEntriesInfo>
                        )}

                        {totalPages > 1 && (
                            <MDPagination>
                                {canPreviousPage && (
                                    <MDPagination item onClick={previousPage}>
                                        <StyledChevronIcon>chevron_left</StyledChevronIcon>
                                    </MDPagination>
                                )}

                                {totalPages > 6 ? (
                                    <StyledPaginationInputContainer>
                                        <MDInput
                                            inputProps={{ type: "number", min: 1, max: totalPages }}
                                            value={pageIndex + 1}
                                            onChange={({ target }) => gotoPage(parseInt(target.value) - 1)}
                                        />
                                    </StyledPaginationInputContainer>
                                ) : (
                                    renderPagination()
                                )}

                                {canNextPage && (
                                    <MDPagination item onClick={nextPage}>
                                        <StyledChevronIcon>chevron_right</StyledChevronIcon>
                                    </MDPagination>
                                )}
                            </MDPagination>
                        )}
                    </StyledPaginationContainer>
                </StyledTableContainer>
            )}
        </>
    );
};

export default DataTable; 